import React from 'react'
import { Row, Col, Card, Statistic, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { StatisticsDataType } from '../../types/statistics'
import { getDevicePackageDisplayName } from '../../utils/devicePackageDisplay'

interface StatisticsSummaryProps {
    statisticsData: StatisticsDataType | null
    versionName?: string
}

const MosquitoIcon: React.FC = () => (
    <svg
        aria-hidden="true"
        data-testid="mosquito-icon"
        focusable="false"
        width="1em"
        height="1em"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M12 7.2V2.6" />
        <path d="m9.8 7.8-2.1-3L4.5 2.7" />
        <path d="m14.2 7.8 2.1-3 3.2-2.1" />
        <path d="M10.1 8.8c.4-1 1.4-1.6 1.9-1.6s1.5.6 1.9 1.6" />
        <path d="M9.9 10.1c0-1.1.9-2 2.1-2s2.1.9 2.1 2" />
        <path d="M12 11.1c1.2 0 2.2 1 2.2 2.2s-1 2.2-2.2 2.2-2.2-1-2.2-2.2 1-2.2 2.2-2.2Z" />
        <path d="M12 15.5v5.2" />
        <path d="M11.9 20.7c-.6 0-1.1-.5-1.1-1.1v-4.9" />
        <path d="M12.1 20.7c.6 0 1.1-.5 1.1-1.1v-4.9" />
        <path d="M9.8 12.4c-4.3-1-7.4-.8-7.8.7-.4 1.5 2.7 2.5 8 1.4" />
        <path d="M14.2 12.4c4.3-1 7.4-.8 7.8.7.4 1.5-2.7 2.5-8 1.4" />
        <path d="M10.4 14.6 7 17.4 5.5 21.4" />
        <path d="M11.1 15.2 9.1 18.2 6.9 22.1" />
        <path d="M13.6 14.6 17 17.4l1.5 4" />
        <path d="M12.9 15.2 14.9 18.2l2.2 3.9" />
    </svg>
)

export const StatisticsSummary: React.FC<StatisticsSummaryProps> = ({
    statisticsData,
    versionName,
}) => {
    const { t } = useTranslation(['statistics', 'device'])
    const versionDetections = statisticsData?.deviceVersionDetections || []
    const speciesSummary = statisticsData?.speciesSummary ?? statisticsData
    const uniquePestTypesTitle = versionName
        ? `${t('metrics.uniquePestTypes')}(${versionName})`
        : t('metrics.uniquePestTypes')
    const getVersionDetectionTitle = (key: string, name: string) => {
        const displayName = getDevicePackageDisplayName(key, name, t)
        return t('metrics.deviceVersionDetectionsTitle', {
            name: displayName,
            defaultValue: `${displayName}数量`,
        })
    }

    return (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} lg={12}>
                <Card style={{ height: '100%' }}>
                    <Row gutter={[16, 16]} align="middle">
                        {versionDetections.map((item) => (
                            <Col xs={24} sm={8} key={item.key}>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 8,
                                        minHeight: 72,
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Typography.Text
                                        type="secondary"
                                        style={{ lineHeight: 1.2 }}
                                    >
                                        {getVersionDetectionTitle(item.key, item.name)}
                                    </Typography.Text>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            fontSize: 20,
                                            fontWeight: 600,
                                            lineHeight: 1,
                                        }}
                                    >
                                        <MosquitoIcon />
                                        <span>{item.value}</span>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Card>
            </Col>
            <Col xs={24} lg={12}>
                <Card style={{ height: '100%' }}>
                    <Statistic
                        title={uniquePestTypesTitle}
                        value={speciesSummary?.uniquePestTypes || 0}
                    />
                </Card>
            </Col>
        </Row>
    )
}
